import React from 'react'
import intl from 'react-intl-universal'
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap'
import localeUtils from '~/reactapp/shared/DatePicker/localeUtils'
import denormalizer from '~/reactapp/story/util/denormalizer'

import { Story, IANSReference, Website } from '~/types'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./SiteSelectorModal.styles.scss')

export interface MssSelection {
  taxonomy: {
    sections: IANSReference[]
  }
  websites: {
    [websiteId: string]: {
      website_section: IANSReference
      website_url?: string
    }
  }
}

interface Props {
  isOpen: boolean
  clockFormat: string
  toggle: () => void
  story: Story
  legacyTaxonomySections: MssSelection['taxonomy']['sections']
  legacyWebsites: MssSelection['websites']
  filteredWebsites: Website[]
  onData: (selectedWebsiteSectionData: MssSelection) => void
  showPublishInfo: boolean
  isStoryPublished: boolean
  showSectionName: boolean
}

export default function SiteSelectorModal(props: Props) {
  const {
    story,
    filteredWebsites,
    clockFormat,
    legacyTaxonomySections,
    legacyWebsites,
    showPublishInfo,
    isStoryPublished,
    toggle,
    showSectionName,
  } = props

  const iframeRef = React.useRef<HTMLIFrameElement>(null)
  const onDataRef = React.useRef(props.onData)
  React.useEffect(() => {
    onDataRef.current = props.onData
  })

  const getPublishInfoString = (publishDate) => {
    if (!publishDate) {
      return intl.get('SiteSelectorModal.publishInfo.notPublished')
    }
    const DATEFORMAT = `${localeUtils.locale.formatLong.date({
      width: 'medium',
    })} ${clockFormat === '12' ? 'h:mm a' : 'HH:mm'}`

    return `${intl.get(
      'SiteSelectorModal.publishInfo.lastPublished'
    )}: ${localeUtils.format(publishDate, DATEFORMAT)}`
  }

  React.useEffect(() => {
    const initSelectorData = (sections) => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            message: 'selectorInitData',
            action: 'circulate',
            data: {
              taxonomy: {
                sections: sections,
              },
              websites: legacyWebsites,
              canonicalWebsite: story.canonical_website,
              filteredSites: filteredWebsites,
            },
          },
          window.location.origin
        )
      }
    }

    function handleMessage(event) {
      if (
        !iframeRef.current ||
        !iframeRef.current.contentWindow ||
        iframeRef.current.contentWindow !== event.source
      ) {
        return
      }

      const { message, data } = event.data

      switch (message) {
        case 'sendSelectorInitData':
          if (showSectionName) {
            denormalizer(legacyTaxonomySections).then(
              (inflatedSectionsResult) => {
                initSelectorData(inflatedSectionsResult)
              }
            )
          } else {
            initSelectorData(legacyTaxonomySections)
          }

          break
        case 'selectorANSData':
          onDataRef.current(data)
          toggle()
          break
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [
    story,
    filteredWebsites,
    legacyTaxonomySections,
    legacyWebsites,
    toggle,
    showSectionName,
  ])

  return (
    <Modal
      wrapClassName="bootstrap4"
      size="lg"
      className={styles.modal}
      centered
      isOpen={props.isOpen}
      toggle={props.toggle}
    >
      <ModalBody>
        <iframe
          ref={iframeRef}
          title="Sites & Sections Selector"
          height="100%"
          width="100%"
          src="/site-selector/"
        />
      </ModalBody>
      <ModalFooter>
        {showPublishInfo && (
          <>
            <span
              className={`fa fa-circle text-${
                isStoryPublished ? 'success' : 'primary'
              }`}
            ></span>
            <div className="mr-auto">
              <span className="publish-info">
                {isStoryPublished
                  ? intl.get('common.storyState.published')
                  : intl.get('common.storyState.draft')}
                : {story?.headlines?.basic ?? ''}
                <br />
              </span>
              <span data-testid="publish-info-string">
                {getPublishInfoString(
                  story.additional_properties?.is_published
                    ? story.additional_properties?.publish_date
                    : null
                )}
              </span>
            </div>
          </>
        )}
        <Button color="secondary" outline onClick={props.toggle}>
          {intl.get('SiteSelectorModal.buttons.cancel')}
        </Button>
        <Button
          data-toggle={showPublishInfo ? 'tooltip' : ''}
          title={
            isStoryPublished
              ? intl.get('SiteSelectorModal.buttons.saveAndCirculateTooltip')
              : intl.get('SiteSelectorModal.buttons.saveTooltip')
          }
          color={isStoryPublished ? 'success' : 'primary'}
          onClick={() => {
            if (iframeRef.current && iframeRef.current.contentWindow) {
              iframeRef.current.contentWindow.postMessage(
                {
                  message: 'sendSelectorANSData',
                },
                window.location.origin
              )
            }
          }}
        >
          {isStoryPublished
            ? intl.get('SiteSelectorModal.buttons.saveAndCirculate')
            : intl.get('SiteSelectorModal.buttons.save')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
